// Caitlyn empowered auto (every 6th shot) — a sniper bullet with a big impact burst.

import type { Animation } from "../../engine/animation/AnimationQueue";
import { Easing } from "../../engine/animation/Easing";
import type { Point } from "../../engine/hex/HexLayout";

const CHARGE_MS = 90;
const TRAVEL_MS = 140;
const IMPACT_MS = 380;
const TOTAL = CHARGE_MS + TRAVEL_MS + IMPACT_MS;

const COLOR = "#ff3030";
const COLOR_BRIGHT = "#ffffff";

interface Particle {
  angle: number;
  speed: number;
  size: number;
}

export class CaitlynHeadshotVFX implements Animation {
  private elapsed = 0;
  private impactFired = false;
  private particles: Particle[];

  constructor(
    private from: Point,
    private to: Point,
    private onImpact?: () => void,
  ) {
    this.particles = Array.from({ length: 16 }, () => ({
      angle: Math.random() * Math.PI * 2,
      speed: 55 + Math.random() * 90,
      size: 2 + Math.random() * 4,
    }));
  }

  update(dt: number): void {
    this.elapsed = Math.min(this.elapsed + dt, TOTAL);
    if (!this.impactFired && this.elapsed >= CHARGE_MS + TRAVEL_MS) {
      this.impactFired = true;
      this.onImpact?.();
    }
  }

  draw(ctx: CanvasRenderingContext2D): void {
    if (this.elapsed < CHARGE_MS) {
      this.drawCharge(ctx);
    } else if (this.elapsed < CHARGE_MS + TRAVEL_MS) {
      this.drawBullet(ctx);
    } else {
      this.drawImpact(ctx);
    }
  }

  private drawCharge(ctx: CanvasRenderingContext2D): void {
    const t = Easing.easeOutQuad(this.elapsed / CHARGE_MS);
    ctx.save();
    ctx.shadowColor = COLOR;
    ctx.shadowBlur = 18 * t;
    ctx.globalAlpha = t * 0.9;
    ctx.fillStyle = COLOR;
    ctx.beginPath();
    ctx.arc(this.from.x, this.from.y, 4 + t * 5, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  private drawBullet(ctx: CanvasRenderingContext2D): void {
    const raw = (this.elapsed - CHARGE_MS) / TRAVEL_MS;
    const t = Easing.easeOutQuad(raw);
    const x = this.from.x + (this.to.x - this.from.x) * t;
    const y = this.from.y + (this.to.y - this.from.y) * t;

    // Long sniper trail — go back 0.4 in t-space
    const tailT = Math.max(0, t - 0.4);
    const tailX = this.from.x + (this.to.x - this.from.x) * tailT;
    const tailY = this.from.y + (this.to.y - this.from.y) * tailT;

    ctx.save();
    ctx.shadowColor = COLOR;
    ctx.shadowBlur = 12;

    ctx.globalAlpha = 0.55;
    ctx.strokeStyle = COLOR;
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.beginPath();
    ctx.moveTo(tailX, tailY);
    ctx.lineTo(x, y);
    ctx.stroke();

    ctx.globalAlpha = 1;
    ctx.fillStyle = COLOR_BRIGHT;
    ctx.beginPath();
    ctx.arc(x, y, 4, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = COLOR;
    ctx.beginPath();
    ctx.arc(x, y, 2.5, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  private drawImpact(ctx: CanvasRenderingContext2D): void {
    const t = (this.elapsed - CHARGE_MS - TRAVEL_MS) / IMPACT_MS;
    const fadeOut = 1 - Easing.easeInQuad(t);
    const ringT = Easing.easeOutQuad(t);

    ctx.save();
    ctx.shadowColor = COLOR;
    ctx.shadowBlur = 28;

    // Outer expanding ring
    const ringR = 8 * (1 + ringT * 7);
    ctx.globalAlpha = fadeOut * 0.85;
    ctx.strokeStyle = COLOR;
    ctx.lineWidth = 2.5;
    ctx.beginPath();
    ctx.arc(this.to.x, this.to.y, ringR, 0, Math.PI * 2);
    ctx.stroke();

    // Inner tighter ring
    if (t < 0.6) {
      const ringR2 = 8 * (1 + ringT * 3.5);
      ctx.globalAlpha = fadeOut * 0.5;
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.arc(this.to.x, this.to.y, ringR2, 0, Math.PI * 2);
      ctx.stroke();
    }

    // White core flash
    const flashR = 12 * (1.3 - t);
    if (flashR > 0) {
      ctx.globalAlpha = fadeOut * 0.95;
      ctx.fillStyle = COLOR_BRIGHT;
      ctx.beginPath();
      ctx.arc(this.to.x, this.to.y, flashR, 0, Math.PI * 2);
      ctx.fill();
    }

    // Spark particles
    for (const p of this.particles) {
      const dist = p.speed * (t * IMPACT_MS / 1000);
      const px = this.to.x + Math.cos(p.angle) * dist;
      const py = this.to.y + Math.sin(p.angle) * dist;
      ctx.globalAlpha = fadeOut * 0.9;
      ctx.fillStyle = t < 0.25 ? COLOR_BRIGHT : COLOR;
      ctx.beginPath();
      ctx.arc(px, py, p.size * (1 - t * 0.65), 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.restore();
  }

  isDone(): boolean {
    return this.elapsed >= TOTAL;
  }
}
